import createFormSender from '../../modules/ajax-form-sender';
import createValidator from '../../modules/validator';
import { removeButtonLoading, setButtonLoading } from '../utils/loading-btn';
import { destroyTelMasks, initTelMasks } from './tel-input-mask';

const SELECTOR = '.js-ajax-form';

const SITE_KEY = 'ysc1_p0V03Ld08KczDaB7DNeaU9O0JxBtrnuj5HFLb1Pgf413905c';
const TOKEN_FIELD_NAME = 'token';
const SITE_KEY_FIELD_NAME = 'site_key';

type FormMapValue = {
    sender: ReturnType<typeof createFormSender>;
    submitFn: EventListener;
    focusFn: EventListener;
    widgetId: string | number | null;
};

type ApiResponse = {
    success?: boolean;
    message?: string;
    [key: string]: unknown;
};

const map = new WeakMap<HTMLFormElement, FormMapValue>();

// ─────────────── helpers: сообщения ───────────────
const showSuccessMessage = (form: HTMLFormElement, message = '') => {
    const successMessage = form.querySelector('.js-form-message__success');
    const successBlock = form.querySelector<HTMLElement>('.js-form-message--success');
    const failureBlock = form.querySelector<HTMLElement>('.js-form-message--failure');

    if (successBlock) successBlock.hidden = false;
    if (failureBlock) failureBlock.hidden = true;
    if (successMessage) (successMessage as HTMLElement).textContent = message;
};

const showFailureMessage = (form: HTMLFormElement, message = '') => {
    const failureMessage = form.querySelector('.js-form-message__failure');
    const successBlock = form.querySelector<HTMLElement>('.js-form-message--success');
    const failureBlock = form.querySelector<HTMLElement>('.js-form-message--failure');

    if (successBlock) successBlock.hidden = true;
    if (failureBlock) failureBlock.hidden = false;
    if (failureMessage) (failureMessage as HTMLElement).textContent = message;
};

const showFormMessages = (form: HTMLFormElement) => {
    const messagesContainer = form.querySelector('.js-form-messages');
    messagesContainer?.classList.remove('form-messages--hidden');
};

const hideFormMessages = (form: HTMLFormElement) => {
    const messagesContainer = form.querySelector('.js-form-messages');
    const successBlock = form.querySelector<HTMLElement>('.js-form-message--success');
    const failureBlock = form.querySelector<HTMLElement>('.js-form-message--failure');

    messagesContainer?.classList.add('form-messages--hidden');
    setTimeout(() => {
        if (successBlock) successBlock.hidden = true;
        if (failureBlock) failureBlock.hidden = true;
    }, 300);
};

const clearAntispamInput = (form: HTMLFormElement) => {
    const checkInput = form.querySelector<HTMLInputElement>('input[name="check_val"]');
    if (checkInput) checkInput.value = '';
};

// ─────────────── очистка формы после успеха ───────────────
function resetFormAfterSuccess(form: HTMLFormElement) {
    if (typeof form.reset === 'function') form.reset();

    form.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
        const type = (input.type || '').toLowerCase();

        if (type === 'hidden') {
            if (input.name === TOKEN_FIELD_NAME) input.value = '';
            return;
        }

        if (type === 'checkbox' || type === 'radio') {
            input.checked = false;
            return;
        }
        input.value = '';
    });

    form.querySelectorAll<HTMLTextAreaElement>('textarea').forEach((ta) => {
        ta.value = '';
    });

    form.querySelectorAll<HTMLSelectElement>('select').forEach((sel) => {
        sel.selectedIndex = 0;
    });

    form.querySelectorAll('.is-invalid, .has-error, .input--error').forEach((el) => {
        el.classList.remove('is-invalid', 'has-error', 'input--error');
    });

    clearAntispamInput(form);
}

// ─────────────── SmartCaptcha утилиты ───────────────
const ensureCaptchaReady = (): Promise<void> => {
    if (window.smartCaptcha) return Promise.resolve();
    return new Promise((resolve) => {
        const t = setInterval(() => {
            if (window.smartCaptcha) {
                clearInterval(t);
                resolve();
            }
        }, 50);
    });
};

function ensureHiddenInput(form: HTMLFormElement, name: string): HTMLInputElement {
    let input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
    }
    return input;
}

// ——— Жёсткая очистка возможных старых полей капчи ———
function purgeLegacyCaptchaFields(form: HTMLFormElement) {
    form
        .querySelectorAll<HTMLInputElement>(
            'input[name="smart-token"], input[name="yandex_smart_token"], input[name="captcha"], input[name="smartCaptchaToken"]'
        )
        .forEach((el) => el.remove());
}

async function prepareCaptchaForForm(form: HTMLFormElement): Promise<string | number | null> {
    const existing = map.get(form)?.widgetId ?? null;
    if (existing != null) return existing;

    let container =
        form.querySelector<HTMLElement>('.js-captcha-container') ||
        form.querySelector<HTMLElement>('#captcha-container');

    if (!container) {
        container = document.createElement('div');
        container.className = 'js-captcha-container';
        form.appendChild(container);
    }
    if (!container.id) {
        container.id = `captcha-${Math.random().toString(36).slice(2)}`;
    }

    await ensureCaptchaReady();
    if (!window.smartCaptcha) return null;

    purgeLegacyCaptchaFields(form);

    const widgetId = window.smartCaptcha.render(container.id, {
        sitekey: SITE_KEY,
        invisible: true,

        callback: async (token: string) => {
            purgeLegacyCaptchaFields(form);

            const tokenInput = ensureHiddenInput(form, TOKEN_FIELD_NAME);
            tokenInput.value = token;
            const siteKeyInput = ensureHiddenInput(form, SITE_KEY_FIELD_NAME);
            siteKeyInput.value = SITE_KEY;

            // Берём адрес запроса прямо из формы
            const endpoint = form.action;
            const method = (form.getAttribute('method') || 'POST').toUpperCase();

            const fd = new FormData(form);

            try {
                const resp = await fetch(endpoint, {
                    method,
                    body: fd,
                    mode: 'cors',
                    credentials: 'omit',
                });

                const raw = await resp.text();
                let data: ApiResponse | null = null;

                if (raw) {
                    const ct = resp.headers.get('content-type')?.toLowerCase() ?? '';
                    const trimmed = raw.trim();
                    const looksJson = ct.includes('application/json') || trimmed.startsWith('{')
                        || trimmed.startsWith('[');
                    if (looksJson) {
                        data = JSON.parse(raw) as ApiResponse;
                    }
                }

                const ok = resp.ok && (data?.success !== false);
                if (!ok) {
                    const msg =
                        (typeof data?.message === 'string' && data.message) ||
                        `Ошибка сервера (HTTP ${resp.status})`;
                    throw new Error(msg);
                }

                showSuccessMessage(form, (data?.message && String(data.message)) || 'Заявка успешно отправлена');
                resetFormAfterSuccess(form);
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'Произошла ошибка при отправке формы';
                showFailureMessage(form, msg);
            } finally {
                const btn = form.querySelector<HTMLButtonElement>('button.btn[type="submit"]');
                if (btn) removeButtonLoading(btn);
                window.smartCaptcha?.reset(map.get(form)?.widgetId ?? undefined);

                showFormMessages(form);
                setTimeout(() => hideFormMessages(form), 5000);
            }
        },



    });

    const prev = map.get(form);
    if (prev) {
        map.set(form, { ...prev, widgetId });
    }

    return widgetId;
}

// ─────────────── init/destroy —──────────────
async function init(container: Element | Document = document) {
    const forms = Array.from(container.querySelectorAll<HTMLFormElement>(SELECTOR));

    forms.forEach((form) => {
        let isSubmitting = false;

        form.setAttribute('method', form.getAttribute('method') || 'POST');

        const validator = createValidator(form, {
            scrollToInvalidInputOptions: {
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            },
        });

        const sender = createFormSender(form, {
            shouldClearInputs: true,
            onBeforeSend: () => {
                clearAntispamInput(form);
                purgeLegacyCaptchaFields(form);
            },
            onSuccess: ({ success, message }) => {
                if (success) {
                    showSuccessMessage(form, message);
                } else {
                    showFailureMessage(form, message);
                }
            },
            onError: () => {
                showFailureMessage(form, 'Произошла ошибка при отправке формы');
            },
        });

        function submitFn(event: Event) {
            if (isSubmitting) return;
            event.preventDefault();

            const isFormValid = validator.validate();
            const submitBtn = form.querySelector<HTMLButtonElement>('button.btn[type="submit"]');
            if (!isFormValid) return;

            isSubmitting = true;
            if (submitBtn) setButtonLoading(submitBtn);

            prepareCaptchaForForm(form)
                .then((widgetId) => {
                    if (widgetId == null || !window.smartCaptcha) {
                        showFailureMessage(form, 'Капча не инициализирована. Обновите страницу и попробуйте ещё раз.');
                        isSubmitting = false;
                        if (submitBtn) removeButtonLoading(submitBtn);
                        return;
                    }
                    window.smartCaptcha.execute(widgetId);
                })
                .catch(() => {
                    showFailureMessage(form, 'Не удалось инициализировать капчу.');
                    if (submitBtn) removeButtonLoading(submitBtn);
                })
                .finally(() => {
                    setTimeout(() => {
                        isSubmitting = false;
                    }, 0);
                });
        }

        function onFocus(this: HTMLInputElement) {
            validator.clearInput(this);
        }

        validator.inputs.forEach((input) => input.addEventListener('focus', onFocus));
        form.addEventListener('submit', submitFn);

        map.set(form, { sender, submitFn, focusFn: onFocus, widgetId: null });
    });

    await initTelMasks(container);
}

function destroy(container: Element | Document = document) {
    destroyTelMasks(container);

    const forms = Array.from(container.querySelectorAll<HTMLFormElement>(SELECTOR));

    forms.forEach((form) => {
        const data = map.get(form);
        if (!data) return;

        form.removeEventListener('submit', data.submitFn);
        Array.from(form.querySelectorAll('input')).forEach((input) => {
            input.removeEventListener('focus', data.focusFn);
        });

        if (data.widgetId != null) {
            window.smartCaptcha?.destroy(data.widgetId);
        }

        map.delete(form);
    });
}

function getInstanceByElement(element: HTMLFormElement) {
    return map.get(element);
}

const _module = { init, destroy, getInstanceByElement };
export default _module;
